// src/components/superadmin/GerenciarClientes.js
import React, { useState, useEffect, useCallback } from 'react';
import { useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure } from '@chakra-ui/react';

/* ---- Utilitário de slug ---- */
const gerarSlug = (nome) =>
    nome
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')   // remove acentos
        .replace(/[^a-z0-9\s-]/g, '')      // mantém letras, números, espaços e hífens
        .trim()
        .replace(/\s+/g, '-')              // espaços → hífens
        .replace(/-+/g, '-');              // hífens duplos → um só

/* ---- Ícones ---- */
const LinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
);
const PlusIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);
const BuildingIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
);
const RefreshIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);

/* ---- Toggle Switch ---- */
const Toggle = ({ checked, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        style={{
            width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', padding: '2px', transition: 'background 0.2s',
            background: checked ? 'linear-gradient(90deg, #2CCB90, #2DB8E1)' : '#e2e8f0',
            position: 'relative', flexShrink: 0,
        }}
    >
        <span style={{
            display: 'block', width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
            transition: 'transform 0.2s', transform: checked ? 'translateX(20px)' : 'translateX(0)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }} />
    </button>
);

/* ---- Estilos compartilhados para inputs (usados dentro do Modal branco) ---- */
const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '12px 14px',
    borderRadius: '12px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    color: '#1e293b', fontSize: '14px',
    fontFamily: "'Inter', -apple-system, sans-serif",
    outline: 'none', transition: 'all 0.2s',
};

const GerenciarClientes = ({ supabase }) => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [clientes, setClientes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [clienteEditando, setClienteEditando] = useState(null);
    const [focusedInput, setFocusedInput] = useState(null);

    const [formData, setFormData] = useState({ nome: '', data_expiracao: '', ativo: true });

    const fetchClientes = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('clientes')
                .select('*, usuarios:usuarios_sistema(count)')
                .order('created_at', { ascending: false });

            if (error) {
                const { data: rpcData, error: rpcError } = await supabase.rpc('listar_todos_clientes');
                if (rpcError) throw rpcError;
                setClientes(rpcData || []);
            } else {
                setClientes(data.map(c => ({ ...c, total_usuarios: c.usuarios?.[0]?.count || 0 })));
            }
        } catch (error) {
            console.error(error);
            toast({ title: 'Erro ao carregar clientes', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsLoading(false);
        }
    }, [supabase, toast]);

    useEffect(() => { fetchClientes(); }, [fetchClientes]);

    const handleNovoCliente = () => {
        setClienteEditando(null);
        setFormData({ nome: '', data_expiracao: '', ativo: true });
        onOpen();
    };

    const handleEditarCliente = (cliente) => {
        setClienteEditando(cliente);
        setFormData({ nome: cliente.nome, data_expiracao: cliente.data_expiracao || '', ativo: cliente.ativo });
        onOpen();
    };

    const handleSalvarCliente = async () => {
        if (!formData.nome) {
            toast({ title: 'Nome obrigatório', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setIsSaving(true);
        try {
            const slug = gerarSlug(formData.nome);
            if (clienteEditando) {
                const { error } = await supabase.from('clientes').update({ nome: formData.nome, slug, data_expiracao: formData.data_expiracao || null, ativo: formData.ativo }).eq('id', clienteEditando.id);
                if (error) throw error;
                toast({ title: 'Cliente atualizado', status: 'success', duration: 3000, isClosable: true });
            } else {
                try {
                    const { error } = await supabase.rpc('criar_cliente_sistema', { p_nome: formData.nome, p_data_expiracao: formData.data_expiracao || null, p_ativo: formData.ativo });
                    if (error) throw error;
                    // Atualiza slug após criar via RPC
                    const { data: newClient } = await supabase.from('clientes').select('id').eq('nome', formData.nome).order('created_at', { ascending: false }).limit(1).maybeSingle();
                    if (newClient) await supabase.from('clientes').update({ slug }).eq('id', newClient.id);
                } catch {
                    const { error } = await supabase.from('clientes').insert({ nome: formData.nome, slug, data_expiracao: formData.data_expiracao || null, ativo: formData.ativo });
                    if (error) throw error;
                }
                toast({ title: 'Cliente criado', status: 'success', duration: 3000, isClosable: true });
            }
            onClose();
            fetchClientes();
        } catch (error) {
            toast({ title: 'Erro ao salvar', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsSaving(false);
        }
    };

    const handleExcluirCliente = async (cliente) => {
        if (cliente.total_usuarios > 0) {
            toast({ title: 'Não é possível excluir', description: 'Este cliente possui usuários cadastrados.', status: 'warning', duration: 5000, isClosable: true });
            return;
        }
        if (!window.confirm(`Excluir "${cliente.nome}"?`)) return;
        try {
            const { error } = await supabase.from('clientes').delete().eq('id', cliente.id);
            if (error) throw error;
            toast({ title: 'Cliente excluído', status: 'info', duration: 3000, isClosable: true });
            fetchClientes();
        } catch (error) {
            toast({ title: 'Erro ao excluir', description: error.message, status: 'error', duration: 5000, isClosable: true });
        }
    };

    const handleToggleAtivo = async (cliente) => {
        try {
            const { error } = await supabase.from('clientes').update({ ativo: !cliente.ativo }).eq('id', cliente.id);
            if (error) throw error;
            toast({ title: cliente.ativo ? 'Cliente desativado' : 'Cliente ativado', status: 'success', duration: 2000, isClosable: true });
            fetchClientes();
        } catch (error) {
            toast({ title: 'Erro ao alterar status', description: error.message, status: 'error', duration: 5000, isClosable: true });
        }
    };

    const total = clientes.length;
    const ativos = clientes.filter(c => c.ativo).length;
    const totalUsuarios = clientes.reduce((s, c) => s + parseInt(c.total_usuarios || 0), 0);

    const getInputStyle = (field) => ({
        ...inputStyle,
        border: focusedInput === field ? '1px solid #2CCB90' : '1px solid #e2e8f0',
        background: focusedInput === field ? '#f0fdf8' : '#f8fafc',
        boxShadow: focusedInput === field ? '0 0 0 3px rgba(44,203,144,0.1)' : 'none',
    });

    return (
        <>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .gc-stat-card { background:#f8fafc; border:1px solid #e8edf2; border-radius:16px; padding:20px 24px; transition:all 0.2s; }
                .gc-stat-card:hover { background:#f1f5f9; border-color:#d1d9e0; transform:translateY(-2px); }
                .gc-btn-primary { display:inline-flex; align-items:center; gap:8px; padding:10px 18px; border-radius:12px; border:none; cursor:pointer; font-size:13px; font-weight:600; font-family:inherit; transition:all 0.2s; background:linear-gradient(135deg,#2CCB90,#2DB8E1); color:#fff; box-shadow:0 4px 16px rgba(44,203,144,0.3); }
                .gc-btn-primary:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(44,203,144,0.45); }
                .gc-btn-primary:active { transform:scale(0.98); }
                .gc-btn-ghost { display:inline-flex; align-items:center; gap:6px; padding:7px 12px; border-radius:9px; border:1px solid #e2e8f0; cursor:pointer; font-size:12px; font-weight:500; font-family:inherit; transition:all 0.15s; background:#ffffff; color:#64748b; }
                .gc-btn-ghost:hover { background:#f1f5f9; color:#0f172a; border-color:#cbd5e1; }
                .gc-btn-danger { display:inline-flex; align-items:center; gap:6px; padding:7px 12px; border-radius:9px; border:1px solid rgba(239,68,68,0.2); cursor:pointer; font-size:12px; font-weight:500; font-family:inherit; transition:all 0.15s; background:transparent; color:#ef4444; }
                .gc-btn-danger:hover { background:rgba(239,68,68,0.06); color:#dc2626; }
                .gc-btn-danger:disabled { opacity:0.25; cursor:not-allowed; }
                .gc-tr:hover td { background:#fafbfc; }
                .gc-badge-ativo { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:100px; font-size:11px; font-weight:600; cursor:pointer; transition:opacity 0.15s; }
                .gc-badge-ativo:hover { opacity:0.8; }
                .gc-modal-input::placeholder { color:#94a3b8; }
                .gc-btn-modal-cancel { padding:10px 20px; border-radius:12px; border:1px solid #e2e8f0; background:#ffffff; color:#64748b; font-size:14px; font-weight:500; cursor:pointer; font-family:inherit; transition:all 0.15s; }
                .gc-btn-modal-cancel:hover { background:#f8fafc; color:#1e293b; }
                .gc-btn-modal-save { padding:10px 24px; border-radius:12px; border:none; background:linear-gradient(135deg,#2CCB90,#2DB8E1); color:#fff; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; transition:all 0.2s; box-shadow:0 4px 16px rgba(44,203,144,0.3); }
                .gc-btn-modal-save:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(44,203,144,0.4); }
                .gc-btn-modal-save:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
                @media (max-width:768px) { .gc-table-col-hide { display:none; } .gc-stats-grid { grid-template-columns: 1fr 1fr !important; } }
                @media (max-width:480px) { .gc-stats-grid { grid-template-columns: 1fr !important; } }
            `}</style>

            {/* Stats */}
            <div className="gc-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
                {[
                    { label: 'Total de Clientes', value: total, sub: 'Cadastrados no sistema', color: '#7c3aed' },
                    { label: 'Clientes Ativos', value: ativos, sub: 'Com acesso liberado', color: '#16a974' },
                    { label: 'Total de Usuários', value: totalUsuarios, sub: 'Em todos os clientes', color: '#0891b2' },
                ].map((s) => (
                    <div key={s.label} className="gc-stat-card">
                        <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</p>
                        <p style={{ margin: '0 0 4px', fontSize: '32px', fontWeight: 800, letterSpacing: '-0.03em', color: s.color }}>{isLoading ? '—' : s.value}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{s.sub}</p>
                    </div>
                ))}
            </div>

            {/* Header da tabela */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Clientes Cadastrados</h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>{isLoading ? '...' : `${clientes.length} ${clientes.length === 1 ? 'cliente' : 'clientes'}`}</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="gc-btn-ghost" onClick={fetchClientes} title="Atualizar">
                        <RefreshIcon /> Atualizar
                    </button>
                    <button className="gc-btn-primary" onClick={handleNovoCliente}>
                        <PlusIcon /> Novo Cliente
                    </button>
                </div>
            </div>

            {/* Tabela */}
            <div style={{ background: '#ffffff', border: '1px solid #e8edf2', borderRadius: '16px', overflow: 'hidden' }}>
                {isLoading ? (
                    <div style={{ padding: '48px', textAlign: 'center' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid rgba(44,203,144,0.2)', borderTopColor: '#2CCB90', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                        <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>Carregando clientes...</p>
                    </div>
                ) : clientes.length === 0 ? (
                    <div style={{ padding: '64px 32px', textAlign: 'center' }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#7c3aed' }}>
                            <BuildingIcon />
                        </div>
                        <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: '#475569' }}>Nenhum cliente cadastrado</p>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#94a3b8' }}>Comece criando o primeiro cliente</p>
                        <button className="gc-btn-primary" onClick={handleNovoCliente}><PlusIcon /> Criar Primeiro Cliente</button>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #e8edf2', background: '#fafbfc' }}>
                                    {['Nome', 'Status', 'Usuários', 'Validade', 'Criado em', ''].map((h, i) => (
                                        <th key={i} style={{ padding: '12px 18px', textAlign: i === 5 ? 'right' : 'left', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}
                                            className={i >= 3 && i <= 4 ? 'gc-table-col-hide' : ''}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {clientes.map((cliente) => (
                                    <tr key={cliente.id} className="gc-tr" style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '14px 18px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', flexShrink: 0 }}>
                                                    <BuildingIcon />
                                                </div>
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{cliente.nome}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '14px 18px' }}>
                                            <span
                                                className="gc-badge-ativo"
                                                onClick={() => handleToggleAtivo(cliente)}
                                                style={{
                                                    background: cliente.ativo ? 'rgba(44,203,144,0.1)' : '#f1f5f9',
                                                    border: `1px solid ${cliente.ativo ? 'rgba(44,203,144,0.25)' : '#e2e8f0'}`,
                                                    color: cliente.ativo ? '#16a974' : '#94a3b8',
                                                }}
                                            >
                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                                                {cliente.ativo ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 18px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '100px', background: 'rgba(8,145,178,0.08)', border: '1px solid rgba(8,145,178,0.2)', color: '#0891b2', fontSize: '12px', fontWeight: 600 }}>
                                                {cliente.total_usuarios}
                                            </span>
                                        </td>
                                        <td className="gc-table-col-hide" style={{ padding: '14px 18px', fontSize: '13px', color: '#64748b' }}>
                                            {cliente.data_expiracao ? new Date(cliente.data_expiracao).toLocaleDateString('pt-BR') : <span style={{ color: '#cbd5e1' }}>Sem expiração</span>}
                                        </td>
                                        <td className="gc-table-col-hide" style={{ padding: '14px 18px', fontSize: '13px', color: '#94a3b8' }}>
                                            {new Date(cliente.created_at).toLocaleDateString('pt-BR')}
                                        </td>
                                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                <button
                                                    className="gc-btn-ghost"
                                                    title="Copiar link público permanente"
                                                    onClick={() => {
                                                        const identificador = cliente.slug || cliente.id;
                                                        const link = `${window.location.origin}/ao-vivo/${identificador}`;
                                                        navigator.clipboard.writeText(link);
                                                        toast({ title: 'Link copiado!', description: link, status: 'success', duration: 3000, isClosable: true });
                                                    }}
                                                    style={{ color: '#16a974', borderColor: 'rgba(44,203,144,0.3)' }}
                                                >
                                                    <LinkIcon />
                                                </button>
                                                <button
                                                    className="gc-btn-ghost"
                                                    title="Copiar link TV (telão)"
                                                    onClick={() => {
                                                        const identificador = cliente.slug || cliente.id;
                                                        const link = `${window.location.origin}/tv/${identificador}`;
                                                        navigator.clipboard.writeText(link);
                                                        toast({ title: 'Link TV copiado!', description: link, status: 'success', duration: 3000, isClosable: true });
                                                    }}
                                                    style={{ color: '#0891b2', borderColor: 'rgba(45,184,225,0.3)' }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                                                </button>
                                                <button className="gc-btn-ghost" onClick={() => handleEditarCliente(cliente)} title="Editar"><EditIcon /></button>
                                                <button className="gc-btn-danger" onClick={() => handleExcluirCliente(cliente)} disabled={cliente.total_usuarios > 0} title="Excluir"><TrashIcon /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
                <ModalOverlay bg="rgba(0,0,0,0.4)" backdropFilter="blur(8px)" />
                <ModalContent
                    bg="#ffffff"
                    border="1px solid #e2e8f0"
                    boxShadow="0 16px 48px rgba(0,0,0,0.12)"
                    borderRadius="20px"
                    color="#0f172a"
                    fontFamily="'Inter', -apple-system, sans-serif"
                    mx={4}
                >
                    <ModalHeader
                        fontSize="18px" fontWeight={700} letterSpacing="-0.02em"
                        borderBottom="1px solid #e2e8f0" pb={4}
                    >
                        {clienteEditando ? 'Editar Cliente' : 'Novo Cliente'}
                    </ModalHeader>
                    <ModalCloseButton color="#94a3b8" _hover={{ color: '#1e293b', bg: '#f8fafc' }} borderRadius="8px" top={4} right={4} />
                    <ModalBody py={6}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                    Nome do Cliente *
                                </label>
                                <input
                                    className="gc-modal-input"
                                    style={getInputStyle('nome')}
                                    placeholder="Nome da empresa"
                                    value={formData.nome}
                                    onChange={e => setFormData({ ...formData, nome: e.target.value })}
                                    onFocus={() => setFocusedInput('nome')}
                                    onBlur={() => setFocusedInput(null)}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                    Data de Expiração <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(opcional)</span>
                                </label>
                                <input
                                    className="gc-modal-input"
                                    type="date"
                                    style={{ ...getInputStyle('data') }}
                                    value={formData.data_expiracao}
                                    onChange={e => setFormData({ ...formData, data_expiracao: e.target.value })}
                                    onFocus={() => setFocusedInput('data')}
                                    onBlur={() => setFocusedInput(null)}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <div>
                                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>Cliente Ativo</p>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Permite acesso ao sistema</p>
                                </div>
                                <Toggle checked={formData.ativo} onChange={v => setFormData({ ...formData, ativo: v })} />
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter gap={3} borderTop="1px solid #e2e8f0" pt={4}>
                        <button className="gc-btn-modal-cancel" onClick={onClose}>Cancelar</button>
                        <button className="gc-btn-modal-save" onClick={handleSalvarCliente} disabled={isSaving}>
                            {isSaving ? 'Salvando...' : clienteEditando ? 'Atualizar' : 'Criar Cliente'}
                        </button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default GerenciarClientes;
